import React, { useState, useEffect } from "react"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import { Checkbox, FormControlLabel, Icon } from "@mui/material"
import ResidentCard from "../ResidentCard"
import { useNavigate } from "react-router-dom"
import Paginator from "../../../Paginator"

import ManageMedicalRecordCreate from "../../../manage/medical-record/create"
import ManageMedicalRecordBulkCreate from "../../../manage/medical-record/bulk-create"
import { ManageDeviceData } from "../../../manage/device/"

export interface ResidentCard {
  id: string
  name: string | null
  initial: string
  status: "normal" | "unset" | "alert" | "warning" | "poc"
  room_no: string
  lastDetectionAt: string
  age: number
  phone: string
  is_poc: number
  devices?: string[]
  device_places: string[]
  detection: number
  log_date: number
  device_state: number
  stop_mail: number
}

interface ResidentCardListProps {
  residents: ResidentCard[]
  reloadData: () => void
  facilityId: string
  devicePlace: string[]
  deviceData: ManageDeviceData[]
  filter: number
}

function ResidentCardList({
  residents,
  reloadData,
  facilityId,
  devicePlace,
  deviceData,
  filter
}: ResidentCardListProps): React.ReactElement {
  const navigate = useNavigate()
  const [filteredData, setFilteredData] = useState<ResidentCard[]>([])

  const [showDetectionOnly, setShowDetectionOnly] = useState(false)
  const [showAlertOnly, setShowAlertOnly] = useState(false)

  // ページャーの状態
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  // 入居者登録モーダルウィンドウ表示
  const [medicalRecordCreate, setMedicalRecordCreate] = useState<boolean>(false)
  const [showAddMedicalRecord, setShowAddMedicalRecord] = useState<boolean>(false)
  const [showCsvDownload, setShowCsvDownload] = useState<boolean>(false)
  const [medicalRecordBulkCreate, setMedicalRecordBulkCreate] = useState<boolean>(false)
  const [changeFilter, setChangeFilter] = useState<boolean>(false)

  // 入居者情報を表示するかどうか
  const [detailInfo, setDetailInfo] = useState<boolean>(false)

  const detectionChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setShowDetectionOnly(checked)
  }

  const alertChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setShowAlertOnly(checked)
  }

  const formatTimestampJST = (timestamp: number): string => {
    const formatter = new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    })

    return formatter
      .format(new Date(timestamp * 1000))
      .replace(/\//g, "-")
      .replace(",", "")
  }

  const getStatusIcon = (status: ResidentCard["status"]): string => {
    switch (status) {
      case "alert":
        return "error_outline"
      case "unset":
        return "pending"
      case "warning":
        return "notificationsNone"
      case "poc":
        return "history_toggle_off"
      default:
        return "check_circle_outline"
    }
  }

  const getStatusColor = (status: ResidentCard["status"]): "primary" | "success" | "info" | "warning" | "dark" => {
    switch (status) {
      case "alert":
        return "primary"
      case "unset":
        return "info"
      case "warning":
        return "warning"
      case "poc":
        return "dark"
      default:
        return "success"
    }
  }

  // ソート処理
  const sortData = (data: ResidentCard[]) => {
    return data.sort((a, b) => {
      const aPriority = (a.detection !== 1 ? 2 : 0) + (a.device_state !== 1 ? 1 : 0)
      const bPriority = (b.detection !== 1 ? 2 : 0) + (b.device_state !== 1 ? 1 : 0)

      // 不在検知・機器エラーの優先順位でソート
      if (aPriority !== bPriority) return bPriority - aPriority

      // 同じ優先順位なら log_date の降順
      return b.log_date - a.log_date
    })
  }

  // 検索処理
  const handleSearch = () => {
    const filtered = residents.filter((record) => {
      let name = record.name
      if (!detailInfo) {
        name = formatInitials(record.initial)
      }

      const matchesText =
        residentName === "" ||
        name?.includes(residentName) ||
        record.phone?.includes(residentName) ||
        record.age?.toString().includes(residentName) ||
        record.room_no?.toString().includes(residentName)

      const matchesDate =
        (startDate === "" || record.log_date >= new Date(startDate + "T00:00:00+09:00").getTime() / 1000) &&
        (endDate === "" || record.log_date <= new Date(endDate + "T00:00:00+09:00").setHours(23, 59, 59, 999) / 1000)

      //console.log("new Date(endDate).getTime() / 1000 :::: ", new Date(endDate).getTime() / 1000)

      const matchesDetection = !showDetectionOnly || (record.detection !== 1 && record.status != "unset")
      const matchesAlert = !showAlertOnly || (record.device_state !== 1 && record.status != "unset")

      return matchesText && matchesDate && matchesDetection && matchesAlert
    })

    const sorted = sortData(filtered)
    setFilteredData(sorted)
    setCurrentPage(1)
  }

  useEffect(() => {
    const sorted = sortData([...residents])
    setFilteredData(sorted)
  }, [residents])

  // Card styling is handled in DefaultInfoCard component

  const handleResidentClick = (facilityId: string, id: string) => {
    navigate(`/tenant/${facilityId}/${id.split("|")[1]}`)
  }

  const [residentName, setResidentName] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  // アカウントに応じたボタン表示処理
  useEffect(() => {
    const auth = localStorage.getItem("auth_facility")
    const auth_account = localStorage.getItem("auth_account")
    if (auth == "1") {
      setShowAddMedicalRecord(true)
    }
    if (auth == "1" || auth_account == "1") {
      setShowCsvDownload(true)
    }

    // 開発者アカウントでの閲覧を可能とする
    const auth_type = localStorage.getItem("account_type")
    if (auth_type == "service") {
      const view_mode = localStorage.getItem("view_mode")
      if (view_mode == "2") {
        const estate_id = localStorage.getItem("show_mode_estate")
        if (estate_id !== "" && estate_id !== "-") {
          setShowAddMedicalRecord(true)
          setDetailInfo(true)
        }
      }
    } else if (auth_type == "estate") {
      setDetailInfo(true)
    }
  }, [])

  // 入居者追加処理
  const handleCreate = () => {
    setMedicalRecordCreate(true)
  }

  // 入居者のCSV一括登録処理
  const handleBulkCreate = () => {
    setMedicalRecordBulkCreate(true)
  }

  const handleClose = async (num: number) => {
    if (num === 1) {
      // APIを再取得
      await reloadData()
      // データ更新後に検索を行う
      setMedicalRecordCreate(false)
      setMedicalRecordBulkCreate(false)
    } else {
      setMedicalRecordCreate(false)
      setMedicalRecordBulkCreate(false)
    }
  }

  // CSV Download
  const handleDownloadCSV = () => {
    // infoが存在する場合にCSVヘッダーを作成
    const headers = [
      "ID",
      "居室番号",
      "氏名",
      "イニシャル"
      ///"年齢",
      //"電話番号",
      //"デバイス設置位置",
    ]

    // CSVデータの生成
    const csvContent = [
      headers.join(","), // ヘッダー行
      ...filteredData.map((item) => {
        const id = JSON.stringify(item?.id) || "" // ID
        const room_no = item?.room_no || "" // 居室番号
        const name = item?.name || "" // 氏名
        const initial = item?.initial || "" // イニシャル

        // データ行を生成
        return [id, room_no, name, initial].join(",")
      })
    ].join("\n")

    // 文字化け防止のためにBOMを追加
    const utf8Bom = "\uFEFF"
    const blob = new Blob([utf8Bom + csvContent], { type: "text/csv;charset=utf-8;" })

    // ファイルをダウンロード
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "medical_record_list.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  // filter の値が変更されたときに検索条件を設定し、検索を実行
  useEffect(() => {
    if (filter === 1) {
      setShowDetectionOnly(false)
      setShowAlertOnly(false)
      setResidentName("")
    } else if (filter === 2) {
      setShowDetectionOnly(true)
      setShowAlertOnly(false)
      setResidentName("")
    } else if (filter === 3) {
      setShowDetectionOnly(false)
      setShowAlertOnly(true)
      setResidentName("")
    }
    // filterが変更されたときに検索を実行
    setChangeFilter(true)
    handleSearch()
  }, [filter]) // filter が変更されたときに実行される

  // 検索条件が変更されたときに検索を再実行する
  useEffect(() => {
    if (changeFilter) {
      handleSearch()
      setChangeFilter(false)
    }
  }, [residentName, showDetectionOnly, showAlertOnly, residents])

  const account_type = localStorage.getItem("account_type")
  const formatInitials = (initial: string): string => {
    if (!initial) return "" // 初期値が空の場合は空文字を返す
    return (
      initial
        .split("") // 1文字ずつ分割
        .filter((char) => char.trim() !== "") // 空白を除外
        .join(".") + "."
    ) // `.` を挿入し、末尾にも `.` を追加
  }

  const validate = (message: string) => {
    setResidentName(message)

    if (message.length > 8) {
      setErrorMessage("入力文字数は8文字以内です")
    } else {
      setErrorMessage("") // エラーがない場合はクリア
    }
  }

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  return (
    <Card>
      {medicalRecordCreate && (
        <ManageMedicalRecordCreate
          facilityId={facilityId}
          deviceData={deviceData}
          devicePlace={devicePlace}
          onClose={handleClose}
        />
      )}
      {medicalRecordBulkCreate && (
        <ManageMedicalRecordBulkCreate facilityId={facilityId} devicePlace={devicePlace} onClose={handleClose} />
      )}
      <MDBox pt={3} px={3} pb={2}>
        <MDTypography variant="h4" fontWeight="medium" display="flex">
          {showAddMedicalRecord && (
            <MDBox
              display="flex"
              gap={1}
              flexWrap="wrap"
              justifyContent="flex-start"
              sx={{
                fontSize: "0.875rem",
                color: "text.primary",
                textAlign: "left",
                width: "100%",
                marginBottom: "10px"
              }}
            >
              <MDBox style={{ fontSize: "1.5rem", fontWeight: "600" }}>入居者一覧</MDBox>
              <MDBox sx={{ width: "calc(100% - 420px)" }} />
              <MDBox>
                <MDButton variant="gradient" color="info" size="medium" onClick={handleBulkCreate}>
                  一括登録
                </MDButton>
              </MDBox>
              <MDBox>
                <MDButton variant="gradient" color="info" size="medium" onClick={handleCreate}>
                  追加
                </MDButton>
              </MDBox>
              <MDBox>
                <MDButton variant="outlined" color="info" size="medium" onClick={handleDownloadCSV}>
                  <Icon>download</Icon>&nbsp; CSV
                </MDButton>
              </MDBox>
            </MDBox>
          )}
          {!showAddMedicalRecord && showCsvDownload && (
            <MDBox
              display="flex"
              gap={1}
              flexWrap="wrap"
              justifyContent="flex-start"
              sx={{
                fontSize: "0.875rem",
                color: "text.primary",
                textAlign: "left",
                width: "100%",
                marginBottom: "10px"
              }}
            >
              <MDBox style={{ fontSize: "1.5rem", fontWeight: "600" }}>入居者一覧</MDBox>
              <MDBox sx={{ width: "calc(100% - 370px)" }} />
              <MDBox>
                <MDButton variant="gradient" color="info" size="medium" onClick={handleDownloadCSV}>
                  CSVダウンロード
                </MDButton>
              </MDBox>
            </MDBox>
          )}
          {!showAddMedicalRecord && !showCsvDownload && (
            <MDBox
              display="flex"
              gap={1}
              flexWrap="wrap"
              justifyContent="flex-start"
              sx={{
                fontSize: "0.875rem",
                color: "text.primary",
                textAlign: "left",
                width: "100%",
                marginBottom: "10px"
              }}
            >
              <MDBox style={{ fontSize: "1.5rem", fontWeight: "600" }}>入居者一覧</MDBox>
            </MDBox>
          )}
        </MDTypography>
        <MDBox display="flex" alignItems="center" sx={{ fontSize: "0.875rem" }} gap={1}>
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="primary"
            variant="gradient"
          />
          機器エラー
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="warning"
            variant="gradient"
            ml={1}
          />
          不在検知
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="info"
            variant="gradient"
            ml={1}
          />
          機器未登録
          <MDBox
            width="20px"
            height="20px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            bgColor="white"
            variant="contained"
            ml={1}
            sx={{ border: "1px solid #ddd" }}
          />
          正常
        </MDBox>
        <MDBox display="flex" flexWrap="wrap" alignItems="left" mt={3} mb={3} gap={3}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <div style={{ position: "relative", display: "inline-block" }}>
              <MDInput
                label="入居者名"
                value={residentName}
                onChange={(e) => validate(e.target.value)}
                sx={{ width: "160px" }}
                error={Boolean(errorMessage)} // エラー時のスタイル変更
              />
              <div
                style={{
                  position: "absolute",
                  top: "100%", // 入力欄の下に配置
                  left: 0,
                  width: "100%",
                  fontSize: "12px",
                  color: "red",
                  marginTop: "4px",
                  whiteSpace: "nowrap"
                }}
              >
                {errorMessage}
              </div>
            </div>
          </MDBox>
          <MDInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={{ width: "160px" }}
          />
          〜
          <MDInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ width: "160px" }} />
          <MDBox display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={<Checkbox defaultChecked checked={showDetectionOnly} onChange={detectionChange} />}
              label="不在検知"
            />
            <FormControlLabel
              control={<Checkbox checked={showAlertOnly} onChange={alertChange} />}
              label="機器エラー"
            />
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            size="medium"
            onClick={() => {
              // 検索処理
              handleSearch()
            }}
            disabled={errorMessage !== ""} // エラーメッセージがある場合は無効化
          >
            <Icon>search</Icon>&nbsp; 検索
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={3}>
            {paginatedData.map((resident) => (
              <Grid key={resident.id} item xs={12} md={6} lg={4} xl={3}>
                {/* 開発者アカウントでの閲覧を可能とする onClickを undefined から CB関数へ変更 */}
                <ResidentCard
                  icon={getStatusIcon(resident.status)}
                  color={getStatusColor(resident.status)}
                  title={!detailInfo ? (formatInitials(resident.initial) ?? "") : (resident.name ?? "")}
                  roomNumber={resident.room_no}
                  lastDetectionAt={formatTimestampJST(resident.log_date)}
                  id={resident.id}
                  stop_mail={resident.stop_mail}
                  onClick={
                    account_type !== "service"
                      ? () => handleResidentClick(facilityId, resident.id)
                      : () => handleResidentClick(facilityId, resident.id)
                  }
                />
              </Grid>
            ))}
          </Grid>
        </MDBox>
        {/* ページャー */}
        <Paginator currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </MDBox>
    </Card>
  )
}

export default ResidentCardList
